import { useEffect, useState } from 'react';
import { Copy } from '@phosphor-icons/react';

/**
 * A command block with a copy button. The check draws itself in (stroke dash)
 * and "Copied" is announced to screen readers. The command is in the
 * prerendered HTML either way; only the button needs JavaScript.
 */
export function CopyCommand({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard refused (permissions, insecure context): the text stays selectable.
    }
  };

  return (
    <div className="pg-cmd-wrap">
      <code className="pg-cmd">{text}</code>
      <button
        type="button"
        className="pg-copy"
        onClick={copy}
        data-copied={copied || undefined}
        aria-label="Copy command"
      >
        <Copy className="pg-copy-idle" size={16} weight="bold" aria-hidden="true" />
        <svg className="pg-copy-done" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M3 8.5l3.2 3.2L13 4.8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <span className="sr-only" aria-live="polite">
        {copied ? 'Copied' : ''}
      </span>
    </div>
  );
}
